import { applyFavicon, brandingUrl } from '@pokerroom/branding/browser';
import { brand } from '../lib/brand';
import { effectiveSiteName } from '@pokerroom/branding';
/**
 * @hook usePageMeta
 * @description Dynamically applies site name (document.title) and favicon from system settings / branding.
 * Should be mounted once in the root layout.
 */
import { deploymentIdentity } from '../lib/deployment';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBrandingByKey } from './useBranding';

export function usePageMeta() {
  // Site name → document.title
  const { data: nameSetting } = useQuery<{ key: string; value: string } | null>({
    queryKey: ['settings', 'site_name'],
    queryFn: async () => {
      try {
        return await api.get<{ key: string; value: string }>('/settings/site_name');
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Favicon from branding
  const { item: faviconItem } = useBrandingByKey('favicon');

  // Build-time lock: when VITE_LOCK_FAVICON=true, skip dynamic API override.
  // Set on prod so the static transparent favicon stays permanently.
  const lockFavicon =
    (import.meta as unknown as { env: { VITE_LOCK_FAVICON?: string } }).env.VITE_LOCK_FAVICON ===
    'true';

  useEffect(() => {
    document.title = deploymentIdentity(
      __DEPLOY_ENV__,
      effectiveSiteName(brand, nameSetting?.value),
    ).title;
  }, [nameSetting]);

  useEffect(() => {
    if (lockFavicon) return;
    return applyFavicon(
      brandingUrl('favicon', faviconItem?.updatedAt) || brand.favicon,
      brand.favicon,
    );
  }, [faviconItem, lockFavicon]);
}
